import React, { useState, useEffect, useRef, useContext } from "react";
import InputText from "../../components/ui/input_text/InputText";
import InputDate from "../../components/ui/input_date/InputDate";
import styles from "./styles/Home.module.css";
import InputCheck from "../../components/ui/input_check/InputCheck";
import Button from "../../components/ui/button/Button";
import { useTranslation } from "react-i18next";
import DataContext from "../../context/DataContext";
import { capitalizeFirstLetter } from "../../utils/utils";
import OutputWindow from "../../components/ui/output_window/OutputWindow";
import CloseButton from "../../components/ui/close_button/CloseButton";
import useClickOutside from "../../hooks/useClickOutside";
import InfoButton from "../../components/ui/info_button/InfoButton";
import DialogCloseButton from "../../components/ui/dialog_close_button/DialogCloseButton";

/**
 * InputForm component
 */
const InputForm = ({
    ticket, // {Object} Ticket data
    handleChange, // {Function} Handling input changes
    handleSubmit, // {Function} Handling form submission
    formButtonDisable, // {Boolean} Disable the form button
    resetFields, // {Function} Reset form fields, function
    ticketNumberRef, // {React.RefObject<HTMLElement>} Ticket number input field reference
    formButtonDisableRef,
    dateresetTrigger,
}) => {
    const { t } = useTranslation(); // Using translation hook
    const [hoverFlag, setHoverFlag] = useState(false); // State to manage hover flag
    const hoverTimerRef = useRef(null);

    const [notReadyView, setNotReadyView] = useState(true);
    const [showHelp, setShowHelp] = useState(false);
    const { isServiceDataAvailable, isLoading, homePath, currentCustom } =
        useContext(DataContext);

    useEffect(() => {
        if (isServiceDataAvailable && !isLoading) {
            const timer = setTimeout(() => {
                setNotReadyView(false);
            }, 50);

            // Cleanup function
            return () => {
                clearTimeout(timer);
            };
        }
    }, [isServiceDataAvailable, isLoading]);

    /**
     * Focus on ticket number input field when the component mounts
     */
    useEffect(() => {
        ticketNumberRef.current && ticketNumberRef.current.focus();
    }, []);

    useEffect(() => {
        if (hoverFlag) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = setTimeout(() => {
                setHoverFlag(false);
            }, 2000);
        }
    }, [ticket]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.code === "Enter") {
                if (!formButtonDisableRef.current) {
                    event.preventDefault();
                    handleSubmit();
                } else {
                    setHoverFlag(true);

                    clearTimeout(hoverTimerRef.current);
                    hoverTimerRef.current = setTimeout(() => {
                        setHoverFlag(false);
                    }, 2000);
                }
            }

            if (event.code === "Escape") {
                event.preventDefault();
                resetFields();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            clearTimeout(hoverTimerRef.current); // cleanup on unmount
        };
    }, [ticket]);

    const toggleHelp = () => {
        setShowHelp((prev) => !prev);
    };

    const printOutRef = useRef();

    useClickOutside(printOutRef, toggleHelp);

    return (
        <div
            className={`${styles.main_form_container} ${styles.search_form_container} transition-container  ${
                notReadyView ? "transition-hidden" : "transition-visible"
            } v-flex-container`}
        >
            {showHelp ? (
                <div
                    className={`${styles.printout_main_container}`}
                    ref={printOutRef}
                >
                    <div className={`${styles.printout_close_container}`}>
                        {/* <CloseButton
                            standardButton={true}
                            size={"small"}
                            onClickFuction={toggleHelp}
                        /> */}
                        <DialogCloseButton
                            onClickFuction={toggleHelp}
                            inputId={"helpCloseButton"}
                            size={"small"}
                        />
                    </div>

                    <div className={`${styles.printout_container}`}>
                        <img
                            src={currentCustom?.printOut?.content}
                            alt="Ticket printout"
                        />
                    </div>
                </div>
            ) : (
                <div className={`${styles.form_content_container}`}>
                    <div
                        className={`${styles.form_header_container} flex-container`}
                    >
                        <h1 className={`${styles.form_header}`}>
                            {t(`search tickets`)}
                        </h1>
                    </div>
                    <form
                        className={`${styles.form_container} v-flex-container`}
                        noValidate={true} // Disable default HTML5 validation
                    >
                        {currentCustom?.printOut?.content ? (
                            <div className={`${styles.help_button_container} `}>
                                <InfoButton
                                    onClickFuction={toggleHelp}
                                    inputId={"ticketNumberHelp"}
                                />
                            </div>
                        ) : (
                            <></>
                        )}
                        <div className={`${styles.form_inputs_container}`}>
                            {/* Required validation, hovering the submit button triggers the hoverFlag and if the other conditions are met the filed will be highlited red */}
                            <div
                                className={`${styles.input_container} flex-container`}
                            >
                                <InputText
                                    ref={ticketNumberRef} // Reference for ticket number input
                                    letterSpacing={".2rem"}
                                    placeholder={t("required field")}
                                    label={`${t("ticket number")}*`}
                                    inputId="ticketNumber"
                                    inputValue={ticket?.ticketNumber}
                                    handleChangeFunction={handleChange}
                                    required={
                                        hoverFlag && !ticket?.ticketNumber
                                    } // Required validation
                                />
                                {/* <div
                                    className={`${styles.help_button_container} `}
                                >
                                    <InfoButton
                                        onClickFuction={toggleHelp}
                                        inputId={"ticketNumberHelp"}
                                    />
                                </div> */}
                            </div>
                            <p className={`${styles.ticket_licence_label}`}>
                                {`${capitalizeFirstLetter(
                                    t("enter one of the following"),
                                )}*`}
                            </p>
                            <div
                                className={`${styles.ticket_licence_container}`}
                            >
                                <div
                                    className={`${styles.input_container} flex-container`}
                                >
                                    <InputText
                                        placeholder={t(`enter plate number`)}
                                        letterSpacing={".2rem"}
                                        label={t(`licence plate`)}
                                        inputId="licencePlate"
                                        inputValue={ticket?.licencePlate}
                                        handleChangeFunction={handleChange}
                                        required={
                                            // Required validation
                                            !(
                                                (!ticket?.ticketDate &&
                                                    ticket?.licencePlate) ||
                                                (ticket?.ticketDate &&
                                                    !ticket?.licencePlate)
                                            ) &&
                                            hoverFlag &&
                                            !(
                                                ticket?.ticketDate &&
                                                ticket?.licencePlate
                                            )
                                        }
                                    />
                                    {/* <div
                                        className={`${styles.help_button_container} `}
                                    >
                                        <InfoButton
                                            onClickFuction={toggleHelp}
                                            inputId={"ticketNumberHelp"}
                                        />
                                    </div> */}
                                </div>

                                <div
                                    className={`${styles.input_container} flex-container`}
                                >
                                    <InputDate
                                        label={t(`ticket date`)}
                                        inputId={"ticketDate"}
                                        inputValue={ticket?.ticketDate}
                                        handleChangeFunction={handleChange}
                                        placeholder={"enter a date"}
                                        clearTrigger={dateresetTrigger}
                                        required={
                                            // Required validation
                                            !(
                                                (!ticket?.ticketDate &&
                                                    ticket?.licencePlate) ||
                                                (ticket?.ticketDate &&
                                                    !ticket?.licencePlate)
                                            ) &&
                                            hoverFlag &&
                                            !(
                                                ticket?.ticketDate &&
                                                ticket?.licencePlate
                                            )
                                        }
                                        toToday={true}
                                    />
                                </div>
                            </div>
                            <InputCheck
                                label={t(`i accept the`)}
                                required={!ticket?.generalTerms && hoverFlag} // Required validation
                                inputId="generalTerms"
                                inputValue={ticket?.generalTerms}
                                handleChangeFunction={handleChange}
                                labelLinkURL={`${homePath}/terms`}
                                // labelLinkURL={"https://www.zebra.com/us/en.html"}
                                labelLinkBody={t(`general terms of service`)}
                            />
                        </div>
                        <div
                            className={`${styles.form_button_container} flex-container`}
                        >
                            <Button
                                body={t(`search`)}
                                type={"button"}
                                disabled={formButtonDisable} // Disable button condition
                                // the value is taken from formButtonDisable function
                                setHoverFlag={setHoverFlag}
                                onClickFuction={(event) => handleSubmit(event)}
                            />
                            <Button
                                body={t(`clear`)}
                                type={"button"}
                                onClickFuction={resetFields}
                            />
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default InputForm;
